"use client"

import type React from "react"
import { useState } from "react"

const dummyProfileData = {
  updatedDateTime: "2025-09-22 10:30:00",
  personalDetails: {
    fullName: "John Doe",
    email: "ryan@example.com",
    role: "Administrator",
    phone: "9876543210",
    wtspNum: "9876543210",
    gender: "Male",
    dob: "1990-01-01",
    applyFor: "Developer",
    currBuildingNo: "123",
    currStreet: "Main Street",
    currArea: "Downtown",
    currCity: "Mumbai",
    currState: "Maharashtra",
    currPin: "400001",
    permBuildingNo: "456",
    permStreet: "Second Street",
    permArea: "Uptown",
    permCity: "Pune",
    permState: "Maharashtra",
    permPin: "411001",
    languages: ["English", "Hindi", "Marathi"],
  },
  qualification: {
    highestQualification: "B.Tech in Computer Science",
    workExperience: [
      { CompanyName: "ABC Corp", ExperienceYears: "2" },
      { CompanyName: "XYZ Ltd", ExperienceYears: "3" },
    ],
  },
  about: {
    questions: [
      { question: "Why do you want this job?", answer: "I am passionate about coding." },
      { question: "What are your strengths?", answer: "Problem-solving, teamwork." },
    ],
  },
  familyMembers: {
    "1": {
      name: "Jane Doe",
      age: "50",
      relationship: "Mother",
      organization: "Homemaker",
      dependentOnYou: "Yes",
    },
    "2": {
      name: "Richard Doe",
      age: "55",
      relationship: "Father",
      organization: "Retired",
      dependentOnYou: "No",
    },
  },
  newIdentityProof: {
    aadharFront: "https://via.placeholder.com/150x100?text=Aadhar+Front",
    aadharBack: "https://via.placeholder.com/150x100?text=Aadhar+Back",
    aadharNumber: "1234-5678-9012",
    panFront: "https://via.placeholder.com/150x100?text=PAN+Front",
    panBack: "https://via.placeholder.com/150x100?text=PAN+Back",
    panNumber: "ABCDE1234F",
    emergencyNumber: "9123456789",
    photo: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/image-NxrB0IKlYwCaQe4czqy0eWT17tHtC5.png",
  },
  references: {
    "1": {
      name: "Alice Smith",
      designation: "Manager",
      organization: "ABC Corp",
      email: "alice.smith@abccorp.com",
      phone: "9988776655",
      connection: "Ex-Manager",
    },
    "2": {
      name: "Bob Johnson",
      designation: "Team Lead",
      organization: "XYZ Ltd",
      email: "bob.johnson@xyzltd.com",
      phone: "8877665544",
      connection: "Colleague",
    },
  },
}

const ViewProfileDetails: React.FC = () => {
  const [profilePhoto, setProfilePhoto] = useState<string | null>(dummyProfileData.newIdentityProof.photo)

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setProfilePhoto(reader.result as string) // This updates the displayed photo
      }
      reader.readAsDataURL(file) // Converts file to displayable URL
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-8 border-b border-gray-200">
          <div className="flex items-start gap-6">
            {/* Profile Photo - Rectangular like LinkedIn */}
            <div className="flex-shrink-0">
              <div className="w-40 h-40 rounded-lg overflow-hidden border border-gray-200"> {/* Increased from w-24 h-24 to w-40 h-40 */}
                <img
                  src={profilePhoto || undefined} // Always use profilePhoto
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="mt-3">
                <label htmlFor="profile-upload" className="cursor-pointer">
                  <div className="bg-orange-600 hover:bg-orange-700 text-white text-xs px-3 py-1.5 rounded-md text-center transition-colors">
                    Change Profile Photo
                  </div>
                  <input
                    id="profile-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Profile Info - LinkedIn style layout */}
            <div className="flex-1">
              <h1 className="text-2xl font-semibold text-gray-900 mb-1">{dummyProfileData.personalDetails.fullName}</h1>
              <p className="text-lg text-gray-700 mb-3">
                {dummyProfileData.personalDetails.role}, {dummyProfileData.personalDetails.applyFor}
              </p>
              <p className="text-sm text-gray-500 mb-4">Former Faculty Staff, Technology</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-900">Current:</span>
                  <div className="text-gray-600">{dummyProfileData.qualification.highestQualification}, Microsoft</div>
                </div>
                <div>
                  <span className="font-medium text-gray-900">Education:</span>
                  <div className="text-gray-600">Harvard University</div>
                </div>
                <div>
                  <span className="font-medium text-gray-900">Websites:</span>
                  <div className="text-blue-600">{dummyProfileData.personalDetails.email}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comprehensive Profile Details Section */}
        <div className="p-8 space-y-8">
          {/* Personal Details Section */}
          <div>
            <h2 className="text-2xl font-semibold text-orange-500 mb-6">Personal Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <span className="font-semibold text-gray-900">Current Address:</span>
                <div className="text-gray-700 ml-0 mt-1">
                  {dummyProfileData.personalDetails.currBuildingNo}, {dummyProfileData.personalDetails.currStreet},
                  <br />
                  {dummyProfileData.personalDetails.currArea}, {dummyProfileData.personalDetails.currCity},<br />
                  {dummyProfileData.personalDetails.currState} - {dummyProfileData.personalDetails.currPin}
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-900">Permanent Address:</span>
                <div className="text-gray-700 ml-0 mt-1">
                  {dummyProfileData.personalDetails.permBuildingNo}, {dummyProfileData.personalDetails.permStreet},
                  <br />
                  {dummyProfileData.personalDetails.permArea}, {dummyProfileData.personalDetails.permCity},<br />
                  {dummyProfileData.personalDetails.permState} - {dummyProfileData.personalDetails.permPin}
                </div>
              </div>
            </div>
          </div>

          {/* Qualification Section */}
          <div>
            <h2 className="text-2xl font-semibold text-orange-500 mb-6">Qualification</h2>
            <div>
              <span className="font-semibold text-gray-900">Highest Qualification:</span>{" "}
              <span className="text-gray-700">{dummyProfileData.qualification.highestQualification}</span>
            </div>
          </div>

          {/* Work Experience Section */}
          <div>
            <h2 className="text-2xl font-semibold text-orange-500 mb-6">Work Experience</h2>
            <div className="space-y-4">
              {dummyProfileData.qualification.workExperience.map((exp, index) => (
                <div key={index}>
                  <div>
                    <span className="font-semibold text-gray-900">Company {index + 1}:</span>{" "}
                    <span className="text-gray-700">{exp.CompanyName}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">Years:</span>{" "}
                    <span className="text-gray-700">{exp.ExperienceYears}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* About Section */}
          <div>
            <h2 className="text-2xl font-semibold text-orange-500 mb-6">About</h2>
            <div className="space-y-4">
              {dummyProfileData.about.questions.map((qa, index) => (
                <div key={index}>
                  <div className="font-semibold text-gray-900">
                    Q{index + 1}: {qa.question}
                  </div>
                  <div className="text-gray-700 mt-1">A: {qa.answer}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Profile Updated Section */}
          <div>
            <h2 className="text-2xl font-semibold text-orange-500 mb-4">Profile Updated</h2>
            <div className="text-gray-700">{dummyProfileData.updatedDateTime}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewProfileDetails
